import { ActionIcon, Group, Stack, Textarea, Tooltip, UnstyledButton } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Archive, ArrowUp, Box, Image as ImageIcon, Layers, Zap } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import type { CatalogImageModel, Job, JobRequest } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import { GlassPanel } from "../../../ui/GlassPanel";
import classes from "../composer/composer.module.css";
import type { DraftShape } from "../draft-state";
import { AdvancedSwitch } from "./AdvancedMode";
import image from "./image.module.css";
import { PromptTemplates } from "./PromptTemplates";
import { SourceThumb } from "./SourceThumb";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId";

// Live's words in Layerize mode (captures gaps/18 to 20; `layerize-form/**`).
export const LAYERIZE_PLACEHOLDER =
  "Optional: Tell us what to separate into layers, or leave blank to detect the main elements.";
export const LAYERIZE_PRICE =
  "Estimated cost is between 18 and 289 tokens — the model decides the final quality, which sets the price.";
const LAYERIZE_PROMPT_MAX = 4000;

/** The catalog's Layerize row, when the team may use it: live's tool shows only then. */
export function layerizeModel(models: readonly CatalogImageModel[]): CatalogImageModel | undefined {
  return models.find((model) => model.job_types.includes("image.layerize"));
}

export interface LayerizeComposerProps {
  /** In a card of its own, not floating (`GlassPanel`'s `inline`, the Pro Studio redesign). */
  inline?: boolean | undefined;
  model: CatalogImageModel;
  /** The picture to split, chosen in live's "Choose image to Layerize". */
  picture: PickedImage;
  /** Live's way out: the picture's x, or the active tool. */
  onSwitchOff: () => void;
  onShapeChange: (shape: DraftShape) => void;
  onStarted: (job: Job) => void;
}

/**
 * Live's Layerize mode of the image composer (spec G13; captures gaps/18 to 20): Advanced off,
 * the optional prompt with live's placeholder, the picture as the one attachment, the products
 * and references off, live's prompt templates for the prompt alone, the layers tool active, the
 * model locked to Seedream 5.0 Pro, and the price as the bolt alone with live's range. Start
 * sends `image.layerize`; the layers land in My Generations, where "Edit layers" opens live's
 * layer editor (G13b).
 */
export function LayerizeComposer(props: LayerizeComposerProps) {
  const { model, picture, onSwitchOff, onShapeChange, onStarted } = props;
  const { teamId, brandId } = useParams({ from: ROUTE });
  const [collapsed, setCollapsed] = useState(false);
  const [prompt, setPrompt] = useState("");
  // My Generations' empty state shows one card: live's one Layerize card.
  useEffect(() => {
    onShapeChange({ outputs: 1, aspectRatio: null });
  }, [onShapeChange]);
  const written = prompt.trim();
  const request: JobRequest = {
    type: "image.layerize",
    model: model.id,
    source: { kind: "asset", asset_id: picture.assetId },
    ...(written === "" ? {} : { prompt }),
  };
  const job = usePricedStart({
    teamId,
    brandId,
    quote: request,
    request,
    onStarted: (started) => {
      onSwitchOff();
      onStarted(started);
    },
  });
  const off = (label: string, icon: ReactNode) => (
    <ActionIcon variant="subtle" size={36} aria-label={label} disabled>
      {icon}
    </ActionIcon>
  );
  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <Group gap={12} wrap="nowrap">
        {off("Products", <Archive size={20} strokeWidth={1.5} />)}
        {off("Reference images", <ImageIcon size={20} strokeWidth={1.5} />)}
        {/* Live's templates still work here, the prompt only (a Layerize takes no references). */}
        <PromptTemplates
          draft={{ prompt, referenceIds: [] }}
          onApply={(applied) => setPrompt(applied)}
        />
        <Tooltip label="Layerize mode is active">
          <ActionIcon
            variant="filled-light"
            size={36}
            aria-label="Turn off Layerize"
            onClick={onSwitchOff}
          >
            <Layers size={20} strokeWidth={1.5} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Group gap={12} wrap="nowrap">
        <Tooltip label="Model locked for Layerize">
          <UnstyledButton
            className={image.lockedModel}
            aria-label={`Model: ${model.name}`}
            aria-disabled
          >
            <Box size={16} strokeWidth={1.5} aria-hidden />
            {model.name}
          </UnstyledButton>
        </Tooltip>
        <Tooltip label={LAYERIZE_PRICE} multiline w={300}>
          <output className={image.layerizePrice} aria-label={LAYERIZE_PRICE}>
            <Zap size={16} aria-hidden />
          </output>
        </Tooltip>
        <ActionIcon
          variant="filled"
          size="lg"
          radius="100%"
          aria-label="Start"
          disabled={!job.canStart}
          loading={job.start.isPending}
          onClick={job.startJob}
        >
          <ArrowUp size={20} />
        </ActionIcon>
      </Group>
    </Group>
  );
  return (
    <GlassPanel
      inline={props.inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      header={<AdvancedSwitch on={false} disabled onChange={() => undefined} />}
      summary={<span className={classes.summary}>Layerize</span>}
      above={undefined}
      footer={footer}
    >
      <Stack gap={4}>
        <Textarea
          variant="unstyled"
          aria-label="Prompt"
          placeholder={LAYERIZE_PLACEHOLDER}
          autosize
          minRows={1}
          maxRows={5}
          maxLength={LAYERIZE_PROMPT_MAX}
          value={prompt}
          onChange={(event) => setPrompt(event.currentTarget.value)}
        />
        <div className={image.thumbs}>
          <SourceThumb
            view={picture}
            removeLabel={`Remove ${picture.name}`}
            onRemove={onSwitchOff}
          />
        </div>
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
      </Stack>
    </GlassPanel>
  );
}
